import React, { FC, useMemo } from "react";
import { AttributeType, GeometryType } from "@evergis/api";
import { Dropdown, IconButton, IconToggle, Input, Tooltip } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { Container, Flex } from "components/Dialogs/EditResource/components/Layer/styled";
import { fieldTypes, geometryFieldTypes } from "evergis/core/attributes/fieldTypes";
import { AttributeError } from "../../../../utils/validateAttributeName";
import { TooltipContent, TooltipLabel } from "../../EditResource/components/Table/styled";
import { AttributeTypeSelect } from "../styled";
import { UpdateAttribute } from "../../../../hooks/attributes/useAttributes";
import { LayerAttribute } from "../../EditResource/components/Layer/LayerAttributeItem";
import { useValueChanged } from "../../EditResource/hooks/useValueChanged";
import { AttributeFormat } from "../../DialogComponents/Attributes/AttributeFormat/AttributeFormat";
import { EditGeometryType } from "../../../../evergis/core/attributes/geometryTypes";
import { useAttributesError } from "../../../../hooks/useAttributesError";

type AttributeWithTypeProps = LayerAttribute & {
  geometryType: GeometryType | EditGeometryType;
  onDelete?: (id: string) => void;
  onChange?: UpdateAttribute<LayerAttribute>;
  error?: AttributeError;
  deleteDisabled?: boolean;
  sysAttribute?: boolean;
  onDeleteSysAttribute?: (id: string) => void;
  onChangeSysAttribute?: UpdateAttribute<LayerAttribute>;
};

// eslint-disable-next-line max-lines-per-function
export const AttributeWithType: FC<AttributeWithTypeProps> = ({
  attributeName,
  id,
  type,
  hasIndex,
  geometryType,
  subType,
  description,
  expression,
  onDelete,
  onChange,
  error,
  deleteDisabled,
  sysAttribute,
  onDeleteSysAttribute,
  onChangeSysAttribute,
}) => {
  const { t } = useTranslation("dialogs");
  const { help, error: attributesError } = useAttributesError(error);
  const isNameChanged = useValueChanged(attributeName);
  const fields = expression ? [...fieldTypes, ...geometryFieldTypes] : fieldTypes;
  const processedFields = !deleteDisabled
    ? fields.filter(value => ![AttributeType.Point, AttributeType.MultiPolygon].includes(value.value as AttributeType))
    : fields;

  const indexesTooltip = useMemo(
    () => (
      <div>
        <TooltipLabel>{t("editResource.attributeIndex")}</TooltipLabel>
        <TooltipContent>{t("editResource.attributeIndexDescription")}</TooltipContent>
      </div>
    ),
    [t],
  );

  return (
    <Container style={{ alignItems: "flex-start", padding: "0.75rem 1 rem" }}>
      <Flex>
        <Input
          type="text"
          value={attributeName}
          width={"14rem"}
          placeholder={t("newLayer.attributeNameLat", { ns: "dialogs" })}
          label={t("newLayer.attributeName", { ns: "dialogs" })}
          disabled={!!expression}
          // eslint-disable-next-line
          help={isNameChanged && error ? attributesError : help}
          onChange={e => {
            onChange(id, { attributeName: e.target.value, columnName: e.target.value });
          }}
          error={isNameChanged && !!error}
        />
      </Flex>
      <Flex style={{ alignItems: "flex-end", marginLeft: "1rem" }}>
        <AttributeTypeSelect>
          <Dropdown
            value={subType || type}
            width={"14rem"}
            label={t("newLayer.attributeType", { ns: "dialogs" })}
            disabled={!!expression}
            options={processedFields.map(value => ({
              ...value,
              text: value.langKey ? t(value.langKey, { ns: "common" }) : value.text,
            }))}
            onChange={val => onChange(id, { type: val[0].value as AttributeType })}
            zIndex={120}
          />
        </AttributeTypeSelect>
        {!expression && (
          <Tooltip arrow maxWidth={"11.25rem"} placement={"top"} content={indexesTooltip} delay={0.5}>
            {ref => (
              <IconToggle
                innerRef={ref}
                kind={"refresh_circle"}
                isSelected={hasIndex}
                onClick={() => onChange(id, { hasIndex: !hasIndex })}
              />
            )}
          </Tooltip>
        )}
        {!(!!expression && !sysAttribute) && (
          <IconButton
            kind={"delete"}
            disabled={deleteDisabled}
            onClick={() => (sysAttribute ? onDeleteSysAttribute(id) : onDelete(id))}
          />
        )}
        {!!expression && !sysAttribute && (
          <Tooltip placement="top" content={<strong>{t("newLayer.requiredAttribute", { ns: "dialogs" })}</strong>}>
            {ref => <IconButton kind={"info"} innerRef={ref} />}
          </Tooltip>
        )}
        <AttributeFormat
          type={type}
          id={id}
          updateAttribute={sysAttribute ? onChangeSysAttribute : onChange}
          isCalculated={!!expression}
          geometryType={geometryType}
          description={description}
        />
      </Flex>
    </Container>
  );
};
